/* eslint-disable @next/next/no-img-element */
import { Order } from '@components/account/types'
import DiscountCode from '@components/cart/CartSideDrawerOverlay/DiscountCode'
import { objectKeys } from '@helpers/utils'
import { CustomOrder } from '../../../helpers/transformOrder'
import currencyConverter from '@helpers/misc/currencyConverter'
import styles from './styles.module.scss'
import { calculateOrderSubtotal } from '../../helpers/calculateOrderSubtotal'

function OrderTransactions({
  transactions
}: {
  transactions: Partial<CustomOrder>['transactions']
}) {
  const successfulTransactions =
    transactions?.filter(transaction => transaction.status === 'SUCCESS') || []

  if (!successfulTransactions.length) return null

  return (
    <span className={styles.OrderSummaryCosts_transactions}>
      {successfulTransactions.map(transaction => {
        return (
          <span key={`${transaction.paymentDetails?.last4}`}>
            {transaction?.paymentDetails?.last4}
            <img src={transaction.paymentIcon?.url} alt="payment icon" />
          </span>
        )
      })}
    </span>
  )
}

const calculateTotalDiscount = (
  order: Order
): {
  totalDiscount: number
  discountType: string
} => {
  if (!order?.discountApplications)
    return { totalDiscount: 0, discountType: '' }

  let totalDiscount = 0
  let discountType = ''

  order.discountApplications.forEach(item => {
    const discount = item.value

    if (discount?.amount) {
      totalDiscount += parseFloat(discount.amount) // Fixed amount discount
      discountType = 'AMOUNT'
    }

    if (discount?.percentage) {
      totalDiscount += discount.percentage
      discountType = 'PERCENTAGE'
    }
  })

  return {
    totalDiscount,
    discountType
  }
}

const OrderDiscount = ({
  order,
  totalDiscount,
  discountType,
  subtotalCost
}: {
  order: Order
  totalDiscount: number
  discountType: string
  subtotalCost: number
}) => {
  if (!totalDiscount) return null

  const orderLevelDiscount = order.discountApplications.find(
    discount =>
      discount?.allocationMethod === 'ACROSS' &&
      discount?.targetSelection === 'ALL'
  )

  if (!orderLevelDiscount) return null

  const discountAmount =
    discountType === 'PERCENTAGE'
      ? (subtotalCost * totalDiscount) / 100
      : totalDiscount

  const discountLabel =
    orderLevelDiscount?.code || orderLevelDiscount?.title || 'Discount Applied'
  const discountPercentage =
    discountType === 'PERCENTAGE' ? `${totalDiscount}%` : ''

  return (
    <p>
      <span className={styles.OrderSummaryCosts_summaryDiscount}>
        <span>Discount</span>
        <span className={styles.OrderSummaryCosts_summaryDiscount__discount}>
          <DiscountCode
            code={`${discountLabel} ${discountPercentage}`.trim()}
            variant="small"
          />
        </span>
      </span>
      <span>{`-${currencyConverter({ price: discountAmount })}`}</span>
    </p>
  )
}

export default function OrderSummaryCosts({ order }: { order: Order }) {
  const orderLevelDiscount = order.discountApplications.find(
    discount =>
      discount?.allocationMethod === 'ACROSS' &&
      discount?.targetSelection === 'ALL'
  )

  const { totalDiscount, discountType } = calculateTotalDiscount(order)
  const isOrderLevelDiscount = objectKeys(orderLevelDiscount || {}).length > 0
  const subtotalCost = calculateOrderSubtotal(
    order,
    totalDiscount,
    discountType
  )

  return (
    <div className={styles.OrderSummaryCosts}>
      <p>
        <span className={styles.OrderSummaryCosts_summarySubtotal}>
          Subtotal
        </span>
        <span>
          {currencyConverter({
            price: subtotalCost,
            currency: order.subtotal?.currencyCode
          })}
        </span>
      </p>
      {isOrderLevelDiscount && (
        <OrderDiscount
          order={order}
          totalDiscount={totalDiscount}
          discountType={discountType}
          subtotalCost={subtotalCost}
        />
      )}
      <p>
        <span>Delivery</span>
        <span>
          {Number(order.totalShipping.amount) !== 0
            ? currencyConverter({
                price: order.totalShipping.amount,
                currency: order.totalShipping.currencyCode
              })
            : 'Free'}
        </span>
      </p>
      <p>
        <strong>Total</strong>
        <strong>
          {currencyConverter({
            price: order.totalPrice.amount,
            currency: order.totalPrice.currencyCode
          })}
        </strong>
      </p>
      <div className={styles.separator}></div>
      <p>
        <span>Payment</span>
        <span className={styles.payments}>
          <OrderTransactions transactions={order.transactions} />
        </span>
      </p>
    </div>
  )
}
